import React, {lazy, Suspense, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import useIsMobile from '../hooks/useIsMobile';
import type {MrzPrefill} from '../utils/mapMrzResult';
import {
    getPublicCheckinForm,
    setFormEmail,
    MemberSlotPayload,
    PublicCheckinForm as PublicCheckinFormData,
    submitMemberSlot,
} from '../api.publicCheckin';
import {
    getManagerGuideOverlay, setGuideAccessMode,
    getPublicCityTaxSummary, createCityTaxCheckoutSession, PublicCityTaxSummary,
    setCityTaxPaymentStatus,
} from '../api.checkinGuide';
import { saveCheckinEntry, CHECKIN_STORAGE_KEY } from '../utils/checkinStorage';
import PersonIcon from '../components/icons/PersonIcon';
import KeyIcon from '../components/icons/KeyIcon';
import {colors} from '../theme';
import Signature from '@uiw/react-signature';
import SignatureQualityNotice from '../components/SignatureQualityNotice';
import { blockTouchScroll, SIGNATURE_SURFACE_STYLE } from '../components/signatureSurface';
import { strokesToSvg } from '../utils/signatureEncoding';
import { analyzeSignatureSvg, canSubmitSignature, SIGNATURE_REFERENCE_HEIGHT } from '../utils/signatureQuality';

const getPublicCheckinErrorMessage = (err: any, fallback: string) => {
    if (err?.response?.status === 404) {
        return 'This check-in form is no longer available. Please restart check-in from the apartment link.';
    }
    if (err?.response?.status === 429) {
        return err.response?.data?.error || 'Too many attempts. Please wait a moment and try again.';
    }
    return err?.response?.data?.message || err?.response?.data?.error || fallback;
};

type MemberSlot = {
    firstName: string;
    surname: string;
    /** Full date of birth as ISO string (e.g. "1990-05-15"). */
    dateOfBirth: string;
    phone: string;
    address: string;
    country: string;
    purposeOfStay: string;
    documentType: string;
    documentNumber: string;
    documentCountry: string;
    visaNumber: string;
    /** EU/CR residence permit number — only used for documentType 'green_passport_eu_residence' */
    residencePermitNumber: string;
    hasDisabilityCard: boolean;
    /** In-memory drawing strokes — used by the canvas component only. NOT sent to backend. */
    signaturePoints: number[][][];
    /** Compact SVG path — stored in DB and sent on submit. Derived from signaturePoints on save. */
    signatureSvg?: string;
};

// ─── Signature encoding helpers ──────────────────────────────────────────────

/**
 * Reconstruct strokes from a compact SVG path string so the canvas can replay them.
 * This is only needed when loading a previously saved signature.
 */
const svgToStrokes = (svg: string): number[][][] => {
    if (!svg || !svg.trim()) return [];
    const strokes: number[][][] = [];
    // Split on M (start of a new stroke), then parse L commands
    const strokeStrings = svg.split(/(?=M)/).filter(Boolean);
    for (const s of strokeStrings) {
        const pts: number[][] = [];
        const tokens = s.replace(/Z$/i, '').match(/[ML][^ML]+/g) || [];
        for (const token of tokens) {
            const parts = token.slice(1).trim().split(/\s+/);
            if (parts.length >= 2) {
                const x = parseFloat(parts[0]);
                const y = parseFloat(parts[1]);
                if (!isNaN(x) && !isNaN(y)) pts.push([x, y]);
            }
        }
        if (pts.length > 0) strokes.push(pts);
    }
    return strokes;
};

const createEmptyMember = (): MemberSlot => ({
    firstName: '',
    surname: '',
    dateOfBirth: '',
    phone: '',
    address: '',
    country: '',
    purposeOfStay: '',
    documentType: 'passport',
    documentNumber: '',
    documentCountry: '',
    visaNumber: '',
    residencePermitNumber: '',
    hasDisabilityCard: false,
    signaturePoints: [],
    signatureSvg: undefined,
});

/** ISO 3166-1 alpha-3 code for Czech Republic. Czech citizens are exempt from Ubyport registration. */
/**
 * Parses a "YYYY-MM-DD" string as a LOCAL midnight Date, not UTC midnight.
 * `new Date("YYYY-MM-DD")` is parsed as UTC per the JS spec, which can shift the
 * effective date by a day in negative-UTC-offset timezones when compared against
 * "now"/local dates, or when reading back .getDate()/.getMonth() for display.
 */
const parseLocalDate = (isoDate: string): Date => {
    const [y, m, d] = isoDate.split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
};

/** Formats a Date as "YYYY-MM-DD" using LOCAL date parts (toISOString() would shift to UTC). */
const toLocalIsoDate = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

/** Local midnight for "today" — comparing against `new Date()` directly would let today's date of
 * birth slip through, since any time earlier today is still "<= now". */
const todayLocalMidnight = (): Date => parseLocalDate(toLocalIsoDate(new Date()));

/** A guest can't have been born today or later — the last valid date of birth is yesterday. */
const maxDateOfBirth = (): Date => {
    const d = todayLocalMidnight();
    d.setDate(d.getDate() - 1);
    return d;
};

const CZECH_REPUBLIC_CODE = 'CZE';

const isCzechCitizen = (countryCode: string) => countryCode === CZECH_REPUBLIC_CODE;

// Tesseract WASM + the trained MRZ model are real weight (~18MB) — only fetched the moment a
// guest actually taps "Scan document", never on initial page load. Must live at module scope,
// not inside the component, or React would re-import on every render.
const DocumentScanner = lazy(() => import('../components/DocumentScanner'));

const hasSignature = (m: MemberSlot): boolean =>
    (Array.isArray(m.signaturePoints) && m.signaturePoints.length > 0) ||
    (typeof m.signatureSvg === 'string' && m.signatureSvg.trim().length > 0);

// Signature is editable by default only when the slot has no saved signature.
const buildSignatureEditableState = (members: MemberSlot[]): Record<number, boolean> => {
    const state: Record<number, boolean> = {};
    members.forEach((m, i) => {
        state[i] = !hasSignature(m);
    });
    return state;
};

/** Trim, collapse internal whitespace, uppercase — mirrors BE normalizeName */
const normalizeName = (value: string): string =>
    value.trim().replace(/\s+/g, ' ').toUpperCase();

/** Strip all whitespace and uppercase — mirrors BE normalizeDocNumber */
const normalizeDocNumber = (value: string): string =>
    value.replace(/\s/g, '').toUpperCase();

/** Allowed characters for names after normalization: uppercase CZ/EN letters, space, apostrophe, dash */
const NAME_CHAR_REGEX = /^[A-ZÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ '\-]+$/;

const isValidNameChar = (value: string): boolean => {
    const v = normalizeName(value);
    return v.length === 0 || NAME_CHAR_REGEX.test(v);
};

/** Document number: min 6, max 30, only A-Z and 0-9 (tested after normalization) */
const isValidDocumentNumber = (value: string): boolean => {
    const v = normalizeDocNumber(value);
    return v.length === 0 || (v.length >= 6 && v.length <= 30 && /^[A-Z0-9]+$/.test(v));
};

/** Visa number: exactly 9 digits if provided */
const isValidVisaNumber = (value: string): boolean => {
    const v = value.trim();
    return v.length === 0 || /^\d{9}$/.test(v);
};

/** documentType values that never require a visa (green passport, with or without residence permit) */
const DOC_TYPE_GREEN_PASSPORT = 'green_passport';
const DOC_TYPE_GREEN_PASSPORT_EU_RESIDENCE = 'green_passport_eu_residence';
const isVisaExemptDocType = (documentType: string): boolean =>
    documentType === DOC_TYPE_GREEN_PASSPORT || documentType === DOC_TYPE_GREEN_PASSPORT_EU_RESIDENCE;

/** Residence permit number: alphanumeric, max 15 chars (tested after normalization) */
const isValidResidencePermitNumber = (value: string): boolean => {
    const v = normalizeDocNumber(value);
    return v.length === 0 || (v.length <= 15 && /^[A-Z0-9]+$/.test(v));
};

/** Address must contain at least one alphabetic character */
const addressContainsAlpha = (value: string): boolean =>
    /[a-zA-ZáčďéěíňóřšťúůýžÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ]/.test(value);

const hasAnyMemberData = (m: MemberSlot): boolean => {
    return Boolean(
        (m?.firstName ?? '').trim()
        || (m?.surname ?? '').trim()
        || (m?.dateOfBirth ?? '').trim()
        || (m?.phone ?? '').trim()
        || (m?.address ?? '').trim()
        || (m?.country ?? '').trim()
        || (m?.purposeOfStay ?? '').trim()
        || (m?.documentType ?? '').trim()
        || (m?.documentNumber ?? '').trim()
        || (m?.documentCountry ?? '').trim()
        || (m?.visaNumber ?? '').trim()
        || (m?.residencePermitNumber ?? '').trim()
        || hasSignature(m)
    );
};

const isMissingSignature = (m: MemberSlot): boolean => hasAnyMemberData(m) && !hasSignature(m);

const toDefaultSignaturePoints = (strokes: number[][][]): Record<string, number[][]> => {
    const safe = Array.isArray(strokes) ? strokes : [];
    return safe.reduce((acc, stroke, index) => {
        if (Array.isArray(stroke) && stroke.length > 0) {
            acc[`path-${index + 1}`] = stroke;
        }
        return acc;
    }, {} as Record<string, number[][]>);
};

/**
 * Client-side colour for a slot card: a presence check of the main required fields only. It is
 * NOT the backend's rule (validateSlotComplete also requires address and checks formats and
 * lengths), so a green card does not guarantee the server will accept the slot.
 * complete  = all required fields filled (green)
 * partial   = at least name filled but some required fields missing (orange)
 * empty     = nothing filled (gray)
 */
const getMemberStatus = (
    m: MemberSlot,
    requiresVisaForCountry: (countryCode: string) => boolean
): 'complete' | 'partial' | 'empty' => {
    const hasName = (m?.firstName ?? '').trim().length > 0 && (m?.surname ?? '').trim().length > 0;
    const hasDateOfBirth = (m?.dateOfBirth ?? '').trim().length > 0;
    const hasDoc = (m?.documentType ?? '').trim().length > 0 && (m?.documentNumber ?? '').trim().length > 0;
    const hasCountry = (m?.country ?? '').trim().length > 0 && (m?.documentCountry ?? '').trim().length > 0;

    // Czech citizens are exempt — no purpose or signature required
    if (isCzechCitizen(m?.country ?? '')) {
        if (hasName && hasDateOfBirth && hasDoc && hasCountry) return 'complete';
        if ((m?.firstName ?? '').trim().length > 0 || (m?.surname ?? '').trim().length > 0) return 'partial';
        return 'empty';
    }

    const hasPurpose = (m?.purposeOfStay ?? '').trim().length > 0;
    const hasVisaIfNeeded = isVisaExemptDocType(m?.documentType ?? '')
        || !requiresVisaForCountry(m?.country ?? '')
        || (m?.visaNumber ?? '').trim().length > 0;
    const hasResidencePermitIfNeeded = (m?.documentType ?? '') !== DOC_TYPE_GREEN_PASSPORT_EU_RESIDENCE
        || (m?.residencePermitNumber ?? '').trim().length > 0;
    const hasSigned = hasSignature(m);

    if (hasName && hasDateOfBirth && hasDoc && hasCountry && hasPurpose && hasVisaIfNeeded && hasResidencePermitIfNeeded && hasSigned) return 'complete';
    if ((m?.firstName ?? '').trim().length > 0 || (m?.surname ?? '').trim().length > 0) return 'partial';
    return 'empty';
};

const DOCUMENT_TYPES: { value: string; cs: string; en: string }[] = [
    { value: 'passport',         cs: 'Cestovní pas',       en: 'Passport' },
    { value: 'id_card',          cs: 'Průkaz totožnosti',  en: 'ID Card' },
    { value: 'residence_permit', cs: 'Povolení k pobytu',  en: 'Residence Permit' },
    { value: 'travel_document',  cs: 'Cestovní doklad',    en: 'Travel Document' },
    { value: DOC_TYPE_GREEN_PASSPORT,              cs: 'Zelený pas',                                en: 'Green Passport' },
    { value: DOC_TYPE_GREEN_PASSPORT_EU_RESIDENCE, cs: 'Zelený pas s povolením k pobytu v EU/ČR',  en: 'Green Passport with EU/CR Residence Permit' },
    { value: 'other',            cs: 'Jiný',               en: 'Other' },
];

const SLOT_STATUS_STYLE: Record<'complete' | 'partial' | 'empty', {
    bg: string; borderColor: string; textColor: string; label: string;
}> = {
    complete: {
        bg: colors.badgeGreenBg,
        borderColor: colors.badgeGreenText,
        textColor: colors.badgeGreenText,
        label: '✓'
    },
    partial: {
        bg: colors.badgeOrangeBg,
        borderColor: colors.badgeOrangeText,
        textColor: colors.badgeOrangeText,
        label: '…'
    },
    empty: {bg: '#f7fafc', borderColor: colors.borderInput, textColor: colors.textMuted, label: ''},
};

type SearchableSelectOption = { value: string; label: string };

const SearchableSelect: React.FC<{
    value: string;
    onChange: (value: string) => void;
    options: SearchableSelectOption[];
    placeholder: string;
    className?: string;
}> = ({ value, onChange, options, placeholder, className }) => {
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const selectedLabel = options.find(o => o.value === value)?.label ?? '';

    const filtered = !search.trim()
        ? options
        : options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <div ref={containerRef} style={{ position: 'relative' }}>
            <input
                className={className}
                value={open ? search : selectedLabel}
                placeholder={placeholder}
                onFocus={() => { setOpen(true); setSearch(''); }}
                onChange={e => { setSearch(e.target.value); setOpen(true); }}
                onClick={() => { if (!open) { setOpen(true); setSearch(''); } }}
                readOnly={!open}
                autoComplete="off"
                style={{ cursor: open ? 'text' : 'pointer' }}
            />
            {open && (
                <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
                    background: '#fff', border: '1px solid #cbd5e0', borderRadius: 6,
                    maxHeight: 220, overflowY: 'auto',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)', marginTop: 2,
                }}>
                    {filtered.length === 0 ? (
                        <div style={{ padding: '8px 12px', color: '#718096', fontSize: '0.875rem' }}>No results</div>
                    ) : filtered.map(opt => (
                        <div
                            key={opt.value}
                            onMouseDown={() => { onChange(opt.value); setOpen(false); setSearch(''); }}
                            style={{
                                padding: '8px 12px', cursor: 'pointer', fontSize: '0.875rem',
                                background: opt.value === value ? '#ebf8ff' : 'transparent',
                                borderBottom: '1px solid #f7fafc',
                            }}
                            onMouseEnter={e => (e.currentTarget.style.background = opt.value === value ? '#ebf8ff' : '#f7fafc')}
                            onMouseLeave={e => (e.currentTarget.style.background = opt.value === value ? '#ebf8ff' : 'transparent')}
                        >
                            {opt.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const getPreferredMappedLabel = (option: {
    displayCZ?: string;
    displayEN?: string;
    submitValue: string
}, prefersCzech: boolean): string => {
    const primary = prefersCzech ? option.displayCZ : option.displayEN;
    const fallback = prefersCzech ? option.displayEN : option.displayCZ;
    return primary || fallback || option.submitValue;
};

const fmtTaxAmount = (n: number): string => (Number.isInteger(n) ? String(n) : n.toFixed(2));

const LANGUAGE_STORAGE_KEY = 'guest-form-language';
const SHARE_CONFIRMED_KEY = (token: string) => `guestbook-share-done-${token}`;
type GuestFormLanguage = 'cs' | 'en';

const GUEST_FORM_TEXT: Record<GuestFormLanguage, Record<string, string>> = {
    cs: {
        title: 'Kniha hostů',
        language: 'Jazyk',
        czech: 'Čeština',
        english: 'English',
        stay: 'Pobyt',
        email: 'E-mail',
        numberOfGuests: 'Počet hostů',
        notes: 'Poznámky',
        members: 'Členové',
        membersHint: 'Vyberte člena, doplňte údaje a kdykoliv uložte. Ostatní členové mohou formulář dokončit později.',
        allGuestsFilled: 'Všech {count} hostů je kompletně vyplněno',
        guestsStillMissing: '{missing} z {total} hostů je ještě potřeba doplnit',
        signaturesMissing: 'Chybí {count} podpis',
        signaturesMissingPlural: 'Chybí {count} podpisy',
        signaturesMissingPluralMany: 'Chybí {count} podpisů',
        firstName: 'Jméno',
        firstNamePlaceholder: 'Franz',
        scanDocumentButton: '📷 Naskenovat doklad',
        scanCaptureLabel: 'Vyfotit',
        scanCapturingLabel: 'Čtu…',
        scanHintLabel: 'Umístěte strojově čitelnou zónu (dole na dokladu) do rámečku a stiskněte Vyfotit.',
        scanTroubleLabel: 'Nedaří se to? Zavřít a vyplnit ručně',
        scanCameraErrorLabel: 'Nepodařilo se získat přístup ke kameře. Údaje můžete i tak vyplnit ručně.',
        scanCloseAriaLabel: 'Zavřít skenování',
        fromScanTag: 'ze skenu',
        surname: 'Příjmení',
        surnamePlaceholder: 'Kafka',
        dateOfBirth: 'Datum narození',
        dateOfBirthHint: 'Vyberte datum',
        under18: 'Host je mladší 18 let',
        over18Tax: 'Host je 18+ (může platit turistickou daň)',
        disabilityCard: 'Osoba s průkazem ZTP/P (snížená sazba poplatku za pobyt)',
        invalidNameChars: 'Povolena jsou pouze písmena (EN/CZ), mezery, apostrof a pomlčka',
        phone: 'Telefon',
        phonePlaceholder: '+420 777 888 999',
        address: 'Adresa',
        addressPlaceholder: 'Ulice, číslo popisné, město, PSČ',
        addressTooLong: 'Adresa nesmí přesáhnout 128 znaků',
        addressRequired: 'Adresa je povinná',
        country: 'Stát',
        selectCountry: 'Vyberte váš stát',
        visaNumber: 'Číslo víza',
        visaNumberInvalid: 'Číslo víza musí mít přesně 9 číslic',
        residencePermitNumber: 'Povolení k pobytu v EU/ČR',
        residencePermitNumberRequired: 'Číslo povolení k pobytu je povinné',
        residencePermitNumberInvalid: 'Číslo povolení k pobytu: max. 15 znaků, pouze písmena a číslice',
        selectPurpose: 'Vyberte účel pobytu',
        documentType: 'Typ dokladu',
        selectDocumentType: 'Vyberte typ dokladu',
        documentNumber: 'Číslo dokladu',
        documentNumberPlaceholder: 'ABC1234',
        documentNumberInvalid: 'Číslo dokladu: 6–30 znaků, pouze velká písmena A-Z a číslice 0-9',
        documentCountry: 'Stát dokladu',
        selectDocumentCountry: 'Vyberte stát dokladu',
        signatureRequired: 'Podpis (povinný)',
        clearSignature: 'Smazat podpis',
        signatureCaptured: 'Podpis uložen.',
        signatureTooSimple: 'Podpis vypadá jako tečka nebo velmi krátká čárka. Podepište se prosím znovu tak, jak se obvykle podepisujete.',
        signatureLooksSimple: 'Podpis je jedna rovná čára. Pokud se takto obvykle podepisujete, potvrďte to níže; jinak se prosím podepište znovu.',
        signatureNeedsConfirmation: 'Potvrďte prosím, že je to váš obvyklý podpis, nebo se podepište znovu.',
        signatureConfirmedNote: 'Potvrzeno jako váš obvyklý podpis.',
        signAgain: 'Podepsat znovu',
        confirmUsualSignature: 'Toto je můj obvyklý podpis',
        signatureStillRequired: 'Podpis je stále povinný, aby byl tento host kompletní.',
        signatureHint: 'Podepište se myší, dotykem nebo perem.',
        saveGuestForm: 'Uložit formulář hostů',
        editSignature: 'Upravit podpis',
        signatureLockedHint: 'Podpis je uzamcen po ulozeni. Pro upravu kliknete na "Upravit podpis".',
        shareForm: 'Sdílejte tento odkaz s ostatními členy:',
        saveSuccess: 'Formulář byl úspěšně uložen. Později můžete doplnit zbývající hosty.',
        loading: 'Načítám formulář...',
        guest: 'Host',
        fullyFilled: 'Vyplněno kompletně',
        partiallyFilled: 'Vyplněno částečně',
        notFilledYet: 'Zatím nevyplněno',
        signatureMissing: 'Chybí podpis',
        signShort: 'Podpis',
        copyUrl: 'Kopírovat odkaz',
        clearAll: 'Vymazat vše',
        clearAllConfirm: 'Opravdu chcete vymazat všechna data? Tato akce je nevratná.',
        guestFooterAssistance: 'ℹ️ Pokud potřebujete jakoukoli pomoc, neváhejte nás kontaktovat:',
        submitMyRegistration: 'Odeslat moji registraci',
        confirmSubmitTitle: 'Jste si jistí?',
        confirmSubmitMessage: 'Po odeslání nelze vaši registraci upravit. Pokud budete potřebovat změnu, kontaktujte hostitele.',
        confirmSubmitYes: 'Ano, odeslat',
        cancel: 'Zrušit',
        registrationComplete: 'Vaše registrace byla úspěšně odeslána.',
        slotCompleted: 'Zaregistrováno',
        fillYourDetails: 'Vyplňte své údaje →',
        shareFormInfo: 'Sdílejte tento odkaz s ostatními hosty — každý vyplní jen svou vlastní registraci.',
        allRegistered: 'Všech {count} hostů je zaregistrováno',
        stillPending: '{pending} z {total} registrací je ještě potřeba dokončit',
        guideNotReadyHint: 'Průvodce ubytováním bude dostupný, až všichni hosté dokončí svou registraci.',
        viewGuide: 'Zobrazit průvodce ubytováním →',
        step1Title: 'Sdílejte odkaz s celou skupinou',
        step1Subtitle: 'Každý host vyplní pouze svou vlastní registraci.',
        step1DoneLabel: 'Odkaz byl sdílen se skupinou',
        step1ConfirmBtn: 'Sdílel/a jsem odkaz — pokračovat k registraci →',
        step1NativeShare: '📱 Sdílet přes telefon',
        step2Title: 'Registrace hostů',
        step2DoneLabel: 'Všichni hosté zaregistrováni',
        taxStepTitle: 'Místní poplatek',
        taxStepDoneLabel: 'Místní poplatek uhrazen',
        taxConfirming: 'Potvrzujeme platbu…',
        taxSummaryError: 'Nepodařilo se načíst částku k úhradě. Zkuste stránku obnovit.',
        taxNights: 'nocí',
        taxAdults: 'dospělí',
        taxChildren: 'děti',
        taxDisabled: 'ZTP/P',
        taxTotal: 'Celkem',
        taxAlreadyPaidLabel: 'Již zaplaceno',
        taxMissingLabel: 'Chybí doplatit',
        taxPartialNotice: 'Údaje o hostech byly opraveny, což změnilo celkovou částku poplatku. Pro pokračování k průvodci prosím doplaťte rozdíl.',
        taxPayButton: 'Zaplatit kartou →',
        taxPayMissingButton: 'Doplatit chybějící částku →',
        taxPaying: 'Přesměrování na platbu…',
        taxStripeNotice: 'Platbu zpracovává Stripe. Vaše platební údaje neukládáme.',
        taxManagerOverrideLabel: 'Ruční úprava stavu platby',
        taxOverridePaid: 'Zaplaceno',
        taxOverrideExempt: 'Bez poplatku',
        taxOverrideUnpaid: 'Nezaplaceno',
        taxExemptActiveNotice: 'U této rezervace se místní poplatek nepožaduje. Průvodce ubytováním je odemčený bez ohledu na platbu.',
        taxSetAmountLabel: 'Nastavit uhrazenou částku (Kč) — např. pokud host zaplatil jinak, i před vyplněním formuláře',
        taxSetAmountButton: 'Nastavit',
        step3Title: 'Průvodce ubytováním',
        step3LockedHint: 'Průvodce ubytováním bude dostupný po dokončení registrace všech hostů.',
        step3LockedHintTax: 'Průvodce ubytováním bude dostupný po uhrazení místního poplatku.',
        guideLockedPopupBtn: 'Rozumím',
    },
    en: {
        title: 'Guest Form',
        language: 'Language',
        czech: 'Čeština',
        english: 'English',
        stay: 'Stay',
        email: 'Email',
        numberOfGuests: 'Number of Guests',
        notes: 'Notes',
        members: 'Members',
        membersHint: 'Pick a member, fill what you know, and save anytime. Remaining members can be completed later.',
        allGuestsFilled: 'All {count} guests are fully filled',
        guestsStillMissing: '{missing} of {total} guests still need to be filled',
        signaturesMissing: '{count} signature missing',
        signaturesMissingPlural: '{count} signatures missing',
        signaturesMissingPluralMany: '{count} signatures missing',
        firstName: 'First Name',
        firstNamePlaceholder: 'Franz',
        scanDocumentButton: '📷 Scan document',
        scanCaptureLabel: 'Capture',
        scanCapturingLabel: 'Reading…',
        scanHintLabel: 'Line up the machine-readable strip at the bottom of your document with the frame, then tap Capture.',
        scanTroubleLabel: 'Having trouble? Close and enter manually',
        scanCameraErrorLabel: "Couldn't get camera access. You can still fill this in manually.",
        scanCloseAriaLabel: 'Close scanner',
        fromScanTag: 'from scan',
        surname: 'Surname',
        surnamePlaceholder: 'Kafka',
        dateOfBirth: 'Date of Birth',
        dateOfBirthHint: 'Select date',
        under18: 'Guest is under 18',
        over18Tax: 'Guest is 18+ (tourist tax may apply)',
        disabilityCard: 'Person with Disability Card ZTP/P (reduced city tax rate applies)',
        invalidNameChars: 'Only letters (EN/CZ), spaces, apostrophe and dash are allowed',
        phone: 'Phone',
        phonePlaceholder: '+420 777 888 999',
        address: 'Address',
        addressPlaceholder: 'Street, House number, City, Postal code',
        addressTooLong: 'Address must not exceed 128 characters',
        addressRequired: 'Address is required',
        country: 'Country',
        selectCountry: 'Select your country',
        visaNumber: 'Visa Number',
        visaNumberInvalid: 'Visa number must be exactly 9 digits',
        residencePermitNumber: 'EU/CR Residence Permit',
        residencePermitNumberRequired: 'Residence permit number is required',
        residencePermitNumberInvalid: 'Residence permit number: max 15 characters, letters and digits only',
        selectPurpose: 'Select purpose',
        documentType: 'Document Type',
        selectDocumentType: 'Select document type',
        documentNumber: 'Document Number',
        documentNumberPlaceholder: 'ABC1234',
        documentNumberInvalid: 'Document number: 6–30 characters, uppercase A-Z and digits 0-9 only',
        documentCountry: 'Document Country',
        selectDocumentCountry: 'Select document country',
        signatureRequired: 'Hand Signature (required)',
        clearSignature: 'Clear signature',
        signatureCaptured: 'Signature captured.',
        signatureTooSimple: 'The signature looks like a dot or a very short line. Please sign again the way you usually sign.',
        signatureLooksSimple: 'This signature is a single straight line. If that is how you usually sign, confirm below; otherwise sign again.',
        signatureNeedsConfirmation: 'Please confirm this is your usual signature, or sign again.',
        signatureConfirmedNote: 'Confirmed as your usual signature.',
        signAgain: 'Sign again',
        confirmUsualSignature: 'This is my usual signature',
        signatureStillRequired: 'Signature is still required before this guest can be completed.',
        signatureHint: 'Use mouse, touch, or pen to sign.',
        saveGuestForm: 'Save Guest Form',
        editSignature: 'Edit Signature',
        signatureLockedHint: 'Signature is locked after save. Click "Edit Signature" to modify it.',
        shareForm: 'Share this form URL with other members:',
        saveSuccess: 'Form saved successfully. You can continue later and fill remaining guests.',
        loading: 'Loading form...',
        guest: 'Guest',
        fullyFilled: 'Fully filled',
        partiallyFilled: 'Partially filled',
        notFilledYet: 'Not filled yet',
        signatureMissing: 'Signature missing',
        signShort: 'Sign',
        copyUrl: 'Copy URL',
        clearAll: 'Clear All',
        clearAllConfirm: 'Are you sure you want to clear all data? This action cannot be undone.',
        guestFooterAssistance: "ℹ️ If you need any assistance, please don't hesitate to reach out to us:",
        submitMyRegistration: 'Submit My Registration',
        confirmSubmitTitle: 'Are you sure?',
        confirmSubmitMessage: 'Once submitted, you cannot edit your registration. If changes are needed, please contact the host.',
        confirmSubmitYes: 'Yes, Submit',
        cancel: 'Cancel',
        registrationComplete: 'Your registration has been submitted successfully.',
        slotCompleted: 'Registered',
        fillYourDetails: 'Fill your details →',
        shareFormInfo: 'Share this link with other guests — each person fills only their own registration.',
        allRegistered: 'All {count} guests have registered',
        stillPending: '{pending} of {total} registrations still pending',
        guideNotReadyHint: 'The Check-in Guide will be available once all guests have completed their registration.',
        viewGuide: 'View Check-in Guide →',
        step1Title: 'Share this link with your group',
        step1Subtitle: 'Each person fills only their own registration.',
        step1DoneLabel: 'Link shared with the group',
        step1ConfirmBtn: "I've shared it — continue to registration →",
        step1NativeShare: '📱 Share via phone',
        step2Title: 'Guest registration',
        step2DoneLabel: 'All guests registered',
        taxStepTitle: 'City tax',
        taxStepDoneLabel: 'City tax paid',
        taxConfirming: 'Confirming payment…',
        taxSummaryError: 'Could not load the amount due. Please try refreshing the page.',
        taxNights: 'nights',
        taxAdults: 'adults',
        taxChildren: 'children',
        taxDisabled: 'disabled',
        taxTotal: 'Total',
        taxAlreadyPaidLabel: 'Already paid',
        taxMissingLabel: 'Remaining balance',
        taxPartialNotice: 'One of the guest details was corrected, which changed the total tax amount. Please pay the difference to continue to the guide.',
        taxPayButton: 'Pay by card →',
        taxPayMissingButton: 'Pay remaining balance →',
        taxPaying: 'Redirecting to payment…',
        taxStripeNotice: 'Payment is processed by Stripe. We don\'t store your payment details.',
        taxManagerOverrideLabel: 'Manual payment status override',
        taxOverridePaid: 'Paid',
        taxOverrideExempt: 'Exempt',
        taxOverrideUnpaid: 'Unpaid',
        taxExemptActiveNotice: 'City tax is not required for this reservation. The check-in guide unlocks regardless of payment.',
        taxSetAmountLabel: 'Set the amount already paid (CZK) — e.g. if the guest paid another way, even before filling in the form',
        taxSetAmountButton: 'Set amount',
        step3Title: 'Apartment guide',
        step3LockedHint: 'The apartment guide will be available once all guests have completed their registration.',
        step3LockedHintTax: 'The apartment guide will be available once the city tax has been paid.',
        guideLockedPopupBtn: 'Got it',
    },
};

const formatText = (template: string, values: Record<string, string | number>) =>
    template.replace(/\{(\w+)}/g, (_, key) => String(values[key] ?? ''));

const getContactInfo = (form: PublicCheckinFormData | null): { name?: string; email?: string; phone?: string } => {
    if (!form) return {};
    const contactType = form.guestFooterContactType || 'manager';

    if (contactType === 'host') {
        // Fallback to manager if host contact is missing
        const hasHost = form.hostFullName || form.hostEmail || form.hostPhone;
        if (hasHost) {
            return {
                name: form.hostFullName,
                email: form.hostEmail,
                phone: form.hostPhone,
            };
        }
        // fallback to manager
        return {
            name: form.managerFullName,
            email: form.managerEmail,
            phone: form.managerPhone,
        };
    }

    if (contactType === 'contact') {
        return {
            name: form.contactFullName,
            email: form.contactEmail,
            phone: form.contactPhone,
        };
    }

    return {
        name: form.managerFullName,
        email: form.managerEmail,
        phone: form.managerPhone,
    };
};

const PublicCheckinForm: React.FC = () => {
    const {formToken} = useParams<{ formToken: string }>();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const [form, setForm] = useState<PublicCheckinFormData | null>(null);
    /** Masked slot list from server — completed slots have only { slotIndex, isComplete, firstName, surname } */
    const [slots, setSlots] = useState<MemberSlotPayload[]>([]);
    /** Index of the slot currently being edited (draft form visible). null = no slot open. */
    const [editingSlotIndex, setEditingSlotIndex] = useState<number | null>(null);
    /** In-progress personal data for the slot being filled — lives only in client memory until submit */
    const [draft, setDraft] = useState<MemberSlot>(createEmptyMember());
    /** Whether the document-scan camera overlay is open. */
    const [scanning, setScanning] = useState(false);
    /** Draft field keys currently showing their last-scanned value — cleared per-field the moment the guest edits it. */
    const [scanFilledFields, setScanFilledFields] = useState<Set<string>>(new Set());
    const [confirmSubmit, setConfirmSubmit] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [showValidation, setShowValidation] = useState(false);
    const [language, setLanguage] = useState<GuestFormLanguage>(() => {
        const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
        if (stored === 'cs' || stored === 'en') return stored;
        return window.navigator.language?.toLowerCase().startsWith('cs') ? 'cs' : 'en';
    });
    const signatureRef = useRef<any>(null);
    // Accumulate strokes in refs to avoid re-renders during drawing (which causes block cursor)
    const pendingStrokeRef = useRef<number[][] | null>(null);
    const completedStrokesRef = useRef<number[][][]>([]);
    const [signatureHasStrokes, setSignatureHasStrokes] = useState(false);
    // The SVG as of the last committed stroke (drives the notice) and the exact SVG the guest
    // confirmed as their usual signature. Any new stroke changes the first, so a confirmation
    // never carries over to a different drawing.
    const [signatureSvgNow, setSignatureSvgNow] = useState('');
    const [signatureConfirmedSvg, setSignatureConfirmedSvg] = useState<string | null>(null);

    const [isOwner, setIsOwner] = useState(false);
    const [copySuccess, setCopySuccess] = useState<string | null>(null);
    const [shareConfirmed, setShareConfirmed] = useState(false);
    const [shareCopySuccess, setShareCopySuccess] = useState(false);
    const [showGuideLockedPopup, setShowGuideLockedPopup] = useState(false);

    // ── City tax step (step 3, when the apartment collects it) ────────────────
    const [taxSummary, setTaxSummary] = useState<PublicCityTaxSummary | null>(null);
    const [taxSummaryLoading, setTaxSummaryLoading] = useState(false);
    const [taxSummaryError, setTaxSummaryError] = useState(false);
    const [payingTax, setPayingTax] = useState(false);
    const [confirmingTaxPayment, setConfirmingTaxPayment] = useState(false);
    const [settingTaxStatus, setSettingTaxStatus] = useState(false);
    const [taxAmountInput, setTaxAmountInput] = useState('');

    // Email capture — only shown when form has no email (manager-reset forms sent directly to guest)
    const [emailDraft, setEmailDraft] = useState('');
    const [emailSubmitting, setEmailSubmitting] = useState(false);
    const [emailError, setEmailError] = useState<string | null>(null);

    const t = GUEST_FORM_TEXT[language];
    const prefersCzech = language === 'cs';

    const dedupedStates = useMemo(() => {
        const seen = new Set<string>();
        return (form?.states || []).filter((s) => {
            const key = `${s.submitValue}|${s.displayCZ || ''}|${s.displayEN || ''}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [form?.states]);

    const dedupedPurposes = useMemo(() => {
        const seen = new Set<string>();
        return (form?.purposes || []).filter((p) => {
            const key = `${p.submitValue}|${p.displayCZ || ''}|${p.displayEN || ''}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [form?.purposes]);

    const countryOptions = useMemo<SearchableSelectOption[]>(() => [
        { value: CZECH_REPUBLIC_CODE, label: prefersCzech ? 'Česká republika' : 'Czech Republic / Česká republika' },
        ...dedupedStates.map(state => ({
            value: state.submitValue,
            label: getPreferredMappedLabel(state, prefersCzech),
        })),
    ], [dedupedStates, prefersCzech]);

    /** Real, selectable country codes — a scanned nationality/issuing-country only gets applied
     * to the form when it matches one of these, never guessed. */
    const validStateCodes = useMemo(
        () => new Set([CZECH_REPUBLIC_CODE, ...dedupedStates.map(state => state.submitValue)]),
        [dedupedStates],
    );

    const requiresVisa = (selectedSubmitValue: string): boolean => {
        if (!selectedSubmitValue || dedupedStates.length === 0) return false;
        const found = dedupedStates.find((s) => s.submitValue === selectedSubmitValue);
        return (found?.needVisa ?? 0) === 1;
    };

    useEffect(() => {
        const run = async () => {
            if (!formToken) return;
            setLoading(true);
            setError(null);
            try {
                const data = await getPublicCheckinForm(formToken);
                setForm(data);
                if (data.publicCheckinKey) {
                    localStorage.setItem(`guestbook-form-pk-${formToken}`, data.publicCheckinKey);
                    // Recognize this browser next time it opens the apartment's permanent link,
                    // even if the guest reached this form via an emailed/shared link rather than
                    // Step 1 on this device.
                    saveCheckinEntry(data.publicCheckinKey, {
                        formToken,
                        checkInDate: data.checkInDate,
                        checkOutDate: data.checkOutDate,
                        numberOfGuests: data.numberOfGuests,
                    });
                }
                // Members from server: completed = { slotIndex, isComplete, firstName, surname }
                //                      empty    = { slotIndex, isComplete: false }
                const count = Math.max(1, data.numberOfGuests || 1);
                const serverSlots = data.members ?? [];
                // Pad to the expected count so empty placeholders are shown
                const padded: MemberSlotPayload[] = Array.from({ length: count }, (_, i) => {
                    const found = serverSlots.find(s => s.slotIndex === i);
                    return found ?? { slotIndex: i, isComplete: false };
                });
                setSlots(padded);
                if (formToken) {
                    setShareConfirmed(localStorage.getItem(SHARE_CONFIRMED_KEY(formToken)) === 'true');
                }
            } catch (err: any) {
                if (err?.response?.status === 404 && formToken) {
                    const savedPk = localStorage.getItem(`guestbook-form-pk-${formToken}`);
                    if (savedPk) {
                        // This token is dead (e.g. a manager reset the reservation) — drop the
                        // stale "already checked in" cache too, or the Start page would just
                        // bounce the guest straight back to this same dead link.
                        localStorage.removeItem(CHECKIN_STORAGE_KEY(savedPk));
                        navigate(`/check-in/${savedPk}`, { replace: true });
                        return;
                    }
                    const jwt = localStorage.getItem('token');
                    if (jwt) {
                        setError('This form link has expired. Please go back to Reservations and click "Form" again to generate a new link.');
                    } else {
                        setError('This link is no longer valid. Please ask your host to resend the check-in link.');
                    }
                } else {
                    setError(getPublicCheckinErrorMessage(err, 'Failed to load check-in form'));
                }
            } finally {
                setLoading(false);
            }
            // Silently check if logged-in user owns this apartment
            const token = localStorage.getItem('token');
            if (token && formToken) {
                try {
                    await getManagerGuideOverlay(formToken, token);
                    setIsOwner(true);
                } catch {
                    // 403 = guest view, not an error
                }
            }
        };
        run();
    }, [formToken]);

    const currentUrl = useMemo(() => window.location.href, []);

    useEffect(() => {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    }, [language]);

    // Once registration is fully done on an apartment that collects city tax, load the amount due
    // right here on this page — this is what lets step 3 render inline instead of redirecting away.
    // For the owner previewing their own link, load it as soon as tax collection applies at all —
    // they need the current paid amount to prefill the override panel even before any guest has
    // started the form (e.g. tax was already collected in cash upfront).
    useEffect(() => {
        if (!formToken || !form?.cityTaxCollectionEnabled || taxSummary) return;
        const allSlotsDone = slots.length > 0 && slots.every(s => s.isComplete);
        if (!isOwner && !allSlotsDone) return;
        let cancelled = false;
        setTaxSummaryLoading(true);
        setTaxSummaryError(false);
        getPublicCityTaxSummary(formToken)
            .then(s => { if (!cancelled) setTaxSummary(s); })
            .catch(() => { if (!cancelled) setTaxSummaryError(true); })
            .finally(() => { if (!cancelled) setTaxSummaryLoading(false); });
        return () => { cancelled = true; };
    }, [formToken, slots, form?.cityTaxCollectionEnabled, taxSummary, isOwner]);

    // Returning from Stripe Checkout (?payment=success) — the webhook usually confirms within a
    // second or two, but isn't guaranteed to have landed by the time the browser redirects back.
    // Poll briefly rather than showing a stale "pay now" state right after the guest already paid.
    useEffect(() => {
        if (!formToken) return;
        if (new URLSearchParams(window.location.search).get('payment') !== 'success') return;
        setConfirmingTaxPayment(true);
        let cancelled = false;
        let attempts = 0;
        const maxAttempts = 8;
        const poll = () => {
            if (cancelled) return;
            attempts += 1;
            getPublicCityTaxSummary(formToken)
                .then(summary => {
                    if (cancelled) return;
                    setTaxSummary(summary);
                    if (summary.paid || attempts >= maxAttempts) {
                        setConfirmingTaxPayment(false);
                        return;
                    }
                    setTimeout(poll, 2000);
                })
                .catch(() => {
                    if (cancelled) return;
                    if (attempts >= maxAttempts) { setConfirmingTaxPayment(false); return; }
                    setTimeout(poll, 2000);
                });
        };
        poll();
        return () => { cancelled = true; };
    }, [formToken]);

    const handlePayTax = async () => {
        if (!formToken) return;
        setPayingTax(true);
        try {
            const { url } = await createCityTaxCheckoutSession(formToken);
            window.location.href = url;
        } catch {
            setPayingTax(false);
        }
    };

    const openSlot = (slotIndex: number) => {
        pendingStrokeRef.current = null;
        completedStrokesRef.current = [];
        setSignatureHasStrokes(false);
        setSignatureSvgNow('');
        setSignatureConfirmedSvg(null);
        const defaultPurpose = dedupedPurposes.find(p => p.submitValue === '10')?.submitValue ?? '';
        setDraft({ ...createEmptyMember(), purposeOfStay: defaultPurpose });
        setEditingSlotIndex(slotIndex);
        setError(null);
        setSuccess(null);
        setConfirmSubmit(false);
        setShowValidation(false);
        // Clear the signature canvas on next tick after mount
        setTimeout(() => signatureRef.current?.clear?.(), 0);
    };

    const handleSetGuideAccessMode = async (mode: 'AUTO' | 'OPEN' | 'BLOCKED') => {
        const token = localStorage.getItem('token');
        if (!token || !formToken || form?.guideAccessMode === mode) return;
        try {
            await setGuideAccessMode(formToken, mode, token);
            setForm(prev => prev ? { ...prev, guideAccessMode: mode } : prev);
        } catch {
            // silent — manager will see no state change
        }
    };

    const handleSetTaxPaymentStatus = async (status: 'PAID' | 'UNPAID' | 'EXEMPT', amount?: number) => {
        const token = localStorage.getItem('token');
        if (!token || !formToken || settingTaxStatus) return;
        setSettingTaxStatus(true);
        try {
            await setCityTaxPaymentStatus(formToken, status, token, amount);
            const [summary, refreshedForm] = await Promise.all([
                getPublicCityTaxSummary(formToken),
                getPublicCheckinForm(formToken),
            ]);
            setTaxSummary(summary);
            setForm(prev => prev ? { ...prev, cityTaxExempt: refreshedForm.cityTaxExempt } : prev);
        } catch {
            // silent — manager will see no state change
        } finally {
            setSettingTaxStatus(false);
        }
    };

    const closeSlot = () => {
        pendingStrokeRef.current = null;
        completedStrokesRef.current = [];
        setSignatureHasStrokes(false);
        setSignatureSvgNow('');
        setSignatureConfirmedSvg(null);
        setEditingSlotIndex(null);
        setDraft(createEmptyMember());
        setConfirmSubmit(false);
        setShowValidation(false);
    };

    const onDraftChange = (key: keyof MemberSlot, value: string | boolean | number[][][]) => {
        setDraft(prev => ({...prev, [key]: value}));
        // Once the guest touches a field themselves, it's no longer "as scanned" — stop tagging it.
        setScanFilledFields(prev => {
            if (!prev.has(key)) return prev;
            const next = new Set(prev);
            next.delete(key);
            return next;
        });
    };

    /** Applies a document scan's result to the draft in one update, and tags which fields it touched. */
    const applyMrzPrefill = (prefill: MrzPrefill) => {
        setDraft(prev => ({...prev, ...prefill}));
        setScanFilledFields(new Set(Object.keys(prefill)));
        setScanning(false);
    };

    /** Small "✓ from scan" tag next to a label — cleared the moment the guest edits that field (see onDraftChange). */
    const scanTag = (field: string) => scanFilledFields.has(field) ? (
        <span style={{
            marginLeft: 7, fontSize: '0.65rem', fontWeight: 700, color: colors.badgeGreenText,
            background: colors.badgeGreenBg, padding: '1px 7px', borderRadius: 999, verticalAlign: 'middle',
        }}>
            ✓ {t.fromScanTag}
        </span>
    ) : null;

    // @uiw/react-signature calls onPointer once per FINISHED stroke, from a document-level pointerup
    // listener (and with an empty array on any pointerup that did not draw), not on every pointermove.
    // The wrapper's own pointerup runs before it, so the stroke must be committed here to be evaluated.
    const onSignaturePointer = (points: number[][]) => {
        if (!points || points.length === 0) return;
        // Defense-in-depth: drop points outside the SVG bounds.
        // The library bug (getBoundingClientRect called per pointermove) is patched via
        // patch-package, but this guard catches any future regression or edge case where
        // a page reflow mid-stroke produces wildly out-of-range coordinates.
        const svgEl = (signatureRef.current as any)?.svg as SVGSVGElement | null;
        let sanitized = points;
        if (svgEl) {
            const { width, height } = svgEl.getBoundingClientRect();
            sanitized = points.filter(([x, y]) => x >= 0 && x <= width && y >= 0 && y <= height);
        }
        if (sanitized.length === 0) return;
        pendingStrokeRef.current = sanitized;
        if (!signatureHasStrokes) setSignatureHasStrokes(true);
        commitPendingStroke();
    };

    // Safety net on the wrapper's pointerup / pointercancel / lostpointercapture: a no-op once
    // onSignaturePointer has committed the stroke, and still correct if a future library version
    // reports a stroke before it ends. pendingStrokeRef is also flushed at submit time.
    const commitPendingStroke = () => {
        if (pendingStrokeRef.current && pendingStrokeRef.current.length > 0) {
            completedStrokesRef.current = [...completedStrokesRef.current, pendingStrokeRef.current];
            pendingStrokeRef.current = null;
            setSignatureSvgNow(strokesToSvg(completedStrokesRef.current));
        }
    };
    const onSignaturePointerUp = commitPendingStroke;

    // The SVG that would be submitted right now, including a stroke pointer-up never committed.
    const currentSignatureSvg = (): string => {
        const strokes = pendingStrokeRef.current && pendingStrokeRef.current.length > 0
            ? [...completedStrokesRef.current, pendingStrokeRef.current]
            : completedStrokesRef.current;
        return strokes.length > 0 ? strokesToSvg(strokes) : (draft.signatureSvg || '');
    };
    const signatureAnalysis = analyzeSignatureSvg(signatureSvgNow || draft.signatureSvg || '');
    const signatureAcceptable = (): boolean => {
        const svg = currentSignatureSvg();
        return canSubmitSignature(analyzeSignatureSvg(svg).verdict, signatureConfirmedSvg, svg);
    };
    const confirmUsualSignature = () => {
        commitPendingStroke();
        setSignatureConfirmedSvg(currentSignatureSvg());
    };

    const clearSignature = () => {
        signatureRef.current?.clear?.();
        pendingStrokeRef.current = null;
        completedStrokesRef.current = [];
        setSignatureHasStrokes(false);
        setSignatureSvgNow('');
        setSignatureConfirmedSvg(null);
        setDraft(prev => ({...prev, signaturePoints: [], signatureSvg: undefined}));
    };

    const isDraftComplete = (): boolean => {
        const normFirst = normalizeName(draft.firstName);
        const normSurname = normalizeName(draft.surname);
        const hasFirstName = normFirst.length > 0;
        const hasSurname = normSurname.length > 0;
        const hasDateOfBirth = draft.dateOfBirth.trim().length > 0;
        const hasDoc = draft.documentType.trim().length > 0 && normalizeDocNumber(draft.documentNumber).length > 0;
        const hasCountry = draft.country.trim().length > 0 && draft.documentCountry.trim().length > 0;
        const nameCharsOk = isValidNameChar(draft.firstName) && isValidNameChar(draft.surname);
        const nameLenOk = normFirst.length <= 50 && normSurname.length <= 50;
        const dobOk = !draft.dateOfBirth || (
            parseLocalDate(draft.dateOfBirth) <= maxDateOfBirth() &&
            parseLocalDate(draft.dateOfBirth) >= parseLocalDate('1899-12-31')
        );
        const docNumOk = isValidDocumentNumber(draft.documentNumber);
        const hasAddress = draft.address.trim().length > 0;
        const addressAlphaOk = !draft.address.trim() || addressContainsAlpha(draft.address);
        const addressLenOk = draft.address.trim().length <= 128;
        if (!nameCharsOk || !nameLenOk || !dobOk || !docNumOk || !hasAddress || !addressAlphaOk || !addressLenOk) return false;
        if (isCzechCitizen(draft.country)) {
            return hasFirstName && hasSurname && hasDateOfBirth && hasDoc && hasCountry;
        }
        const hasPurpose = draft.purposeOfStay.trim().length > 0;
        const hasSigned = (signatureHasStrokes || hasSignature(draft)) && signatureAcceptable();
        const visaExempt = isVisaExemptDocType(draft.documentType);
        const visaOk = visaExempt || !requiresVisa(draft.country) || draft.visaNumber.trim().length > 0;
        const visaNumOk = visaExempt || isValidVisaNumber(draft.visaNumber);
        const residencePermitOk = draft.documentType !== DOC_TYPE_GREEN_PASSPORT_EU_RESIDENCE
            || (draft.residencePermitNumber.trim().length > 0 && isValidResidencePermitNumber(draft.residencePermitNumber));
        return hasFirstName && hasSurname && hasDateOfBirth && hasDoc && hasCountry && hasPurpose && hasSigned && visaOk && visaNumOk && residencePermitOk;
    };

    const getValidationErrors = (): string[] => {
        const errors: string[] = [];
        const cs = language === 'cs';
        const normFirst = normalizeName(draft.firstName);
        const normSurname = normalizeName(draft.surname);
        // Required presence
        if (!normFirst) errors.push(cs ? 'Jméno je povinné' : 'First name is required');
        if (!normSurname) errors.push(cs ? 'Příjmení je povinné' : 'Surname is required');
        // Name format and length
        if (normFirst && !isValidNameChar(draft.firstName)) errors.push(cs ? `Jméno: ${t.invalidNameChars}` : `First name: ${t.invalidNameChars}`);
        if (normFirst && normFirst.length > 50) errors.push(cs ? 'Jméno nesmí přesáhnout 50 znaků' : 'First name must not exceed 50 characters');
        if (normSurname && !isValidNameChar(draft.surname)) errors.push(cs ? `Příjmení: ${t.invalidNameChars}` : `Surname: ${t.invalidNameChars}`);
        if (normSurname && normSurname.length > 50) errors.push(cs ? 'Příjmení nesmí přesáhnout 50 znaků' : 'Surname must not exceed 50 characters');
        // Date of birth
        if (!draft.dateOfBirth.trim()) errors.push(cs ? 'Datum narození je povinné' : 'Date of birth is required');
        else if (parseLocalDate(draft.dateOfBirth) > maxDateOfBirth()) errors.push(cs ? 'Datum narození nemůže být dnes ani v budoucnosti' : 'Date of birth cannot be today or in the future');
        else if (parseLocalDate(draft.dateOfBirth) < parseLocalDate('1899-12-31')) errors.push(cs ? 'Datum narození nemůže být před 31.12.1899' : 'Date of birth cannot be before 31.12.1899');
        // Country
        if (!draft.country.trim()) errors.push(cs ? 'Stát je povinný' : 'Country is required');
        // Document
        if (!draft.documentType.trim()) errors.push(cs ? 'Typ dokladu je povinný' : 'Document type is required');
        if (!normalizeDocNumber(draft.documentNumber)) errors.push(cs ? 'Číslo dokladu je povinné' : 'Document number is required');
        else if (!isValidDocumentNumber(draft.documentNumber)) errors.push(t.documentNumberInvalid);
        if (!draft.documentCountry.trim()) errors.push(cs ? 'Stát dokladu je povinný' : 'Document country is required');
        // Address
        if (!draft.address.trim()) errors.push(t.addressRequired);
        else if (!addressContainsAlpha(draft.address)) errors.push(cs ? 'Adresa musí obsahovat alespoň jedno písmeno' : 'Address must contain at least one letter');
        else if (draft.address.trim().length > 128) errors.push(t.addressTooLong);
        // Czech citizens are exempt — no purpose or signature required
        if (!isCzechCitizen(draft.country)) {
            if (!draft.purposeOfStay.trim()) errors.push(cs ? 'Účel pobytu je povinný' : 'Purpose of stay is required');
            if (!isVisaExemptDocType(draft.documentType)) {
                if (requiresVisa(draft.country) && !draft.visaNumber.trim()) errors.push(cs ? 'Číslo víza je povinné' : 'Visa number is required');
                if (draft.visaNumber.trim() && !isValidVisaNumber(draft.visaNumber)) errors.push(t.visaNumberInvalid);
            }
            if (!signatureHasStrokes && !hasSignature(draft)) errors.push(cs ? 'Podpis je povinný' : 'Signature is required');
            else if (!signatureAcceptable()) {
                errors.push(analyzeSignatureSvg(currentSignatureSvg()).verdict === 'STRAIGHT'
                    ? t.signatureNeedsConfirmation : t.signatureTooSimple);
            }
        }
        if (draft.documentType === DOC_TYPE_GREEN_PASSPORT_EU_RESIDENCE) {
            if (!draft.residencePermitNumber.trim()) errors.push(t.residencePermitNumberRequired);
            else if (!isValidResidencePermitNumber(draft.residencePermitNumber)) errors.push(t.residencePermitNumberInvalid);
        }
        return errors;
    };

    const handleCopyUrl = async () => {
        try {
            await navigator.clipboard.writeText(currentUrl);
            setCopySuccess('✔');
            setTimeout(() => setCopySuccess(null), 1200);
        } catch {
            setCopySuccess('✖');
            setTimeout(() => setCopySuccess(null), 1200);
        }
    };

    const onConfirmSubmitSlot = async () => {
        if (!formToken || editingSlotIndex === null) return;
        setSubmitting(true);
        setError(null);
        try {
            // Flush any stroke that wasn't committed by onPointerUp (pointer-capture issue)
        commitPendingStroke();
        const strokesToSubmit = completedStrokesRef.current.length > 0
                ? completedStrokesRef.current
                : draft.signaturePoints;
            const signatureSvg = strokesToSubmit.length > 0
                ? strokesToSvg(strokesToSubmit)
                : (draft.signatureSvg || '');

            const toOpt = (v: string) => v.trim() || undefined;

            await submitMemberSlot(formToken, editingSlotIndex, {
                firstName: normalizeName(draft.firstName),
                surname: normalizeName(draft.surname),
                dateOfBirth: draft.dateOfBirth || undefined,
                phone: toOpt(draft.phone),
                address: toOpt(draft.address),
                country: draft.country,
                purposeOfStay: draft.purposeOfStay,
                documentType: draft.documentType,
                documentNumber: normalizeDocNumber(draft.documentNumber),
                documentCountry: draft.documentCountry,
                visaNumber: toOpt(draft.visaNumber),
                residencePermitNumber: toOpt(draft.residencePermitNumber),
                hasDisabilityCard: draft.hasDisabilityCard,
                signatureSvg,
                signatureSimpleConfirmed: signatureConfirmedSvg !== null && signatureConfirmedSvg === signatureSvg,
            });

            // Reload slots from server (completed slot now returns name-only)
            const updated = await getPublicCheckinForm(formToken);
            const count = Math.max(1, updated.numberOfGuests || 1);
            const serverSlots = updated.members ?? [];
            const padded: MemberSlotPayload[] = Array.from({ length: count }, (_, i) => {
                const found = serverSlots.find(s => s.slotIndex === i);
                return found ?? { slotIndex: i, isComplete: false };
            });
            setSlots(padded);
            setForm(updated);
            setSuccess(t.registrationComplete);
            closeSlot();

            // If this was the last slot and the apartment has a Guide configured, hand the guest
            // straight to it instead of leaving them on the Form page — unless city tax is due
            // first, in which case step 3 (tax) renders in place instead of redirecting anywhere.
            const justCompletedAll = padded.every(s => s.isComplete) && padded.length > 0;
            if (justCompletedAll && !updated.cityTaxCollectionEnabled && updated.guideEnabled && updated.guideAccessMode !== 'BLOCKED') {
                setTimeout(() => navigate(`/guide/${formToken}`), 1500);
            }
        } catch (err: any) {
            const code = err?.response?.data?.code;
            if (code === 'SIGNATURE_TOO_SIMPLE' || code === 'SIGNATURE_INVALID') setError(t.signatureTooSimple);
            else if (code === 'SIGNATURE_NEEDS_CONFIRMATION') setError(t.signatureNeedsConfirmation);
            else setError(getPublicCheckinErrorMessage(err, 'Failed to submit registration'));
            setConfirmSubmit(false);
        } finally {
            setSubmitting(false);
        }
    };

    const handleSetEmail = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!formToken) return;
        const trimmed = emailDraft.trim();
        if (!trimmed || !trimmed.includes('@')) {
            setEmailError(language === 'cs' ? 'Zadejte platnou e-mailovou adresu' : 'Enter a valid email address');
            return;
        }
        setEmailSubmitting(true);
        setEmailError(null);
        try {
            await setFormEmail(formToken, trimmed);
            // Reload form so email is now set
            const updated = await getPublicCheckinForm(formToken);
            setForm(updated);
        } catch (err: any) {
            setEmailError(err?.response?.data?.message || err?.message || 'Failed to set email');
        } finally {
            setEmailSubmitting(false);
        }
    };

    if (loading) return (
        <div style={{maxWidth: 700, margin: '40px auto', textAlign: 'center', color: '#64748b', padding: 24}}>
            <div style={{fontSize: '1.1rem'}}>{t.loading}</div>
        </div>
    );

    const completedCount = slots.filter(s => s.isComplete).length;
    const totalCount = slots.length;
    const allDone = completedCount === totalCount && totalCount > 0;

    // ── Stepper state ────────────────────────────────────────────────────────
    const step1Done = shareConfirmed || completedCount > 0;
    const guideEnabled = !!(form?.guideEnabled);
    const guideAccessMode = form?.guideAccessMode ?? 'AUTO';
    const taxCollectionEnabled = !!(form?.cityTaxCollectionEnabled);
    const taxExempt = !!(form?.cityTaxExempt);
    const taxRequired = taxCollectionEnabled && !taxExempt;
    const taxPaid = !!(taxSummary?.paid);
    const guideAccessible = isOwner || (guideAccessMode !== 'BLOCKED' && (allDone || guideAccessMode === 'OPEN') && (guideAccessMode === 'OPEN' || !taxRequired || taxPaid));
    const guideStepN = taxRequired ? 4 : 3;

    const confirmShare = () => {
        if (formToken) localStorage.setItem(SHARE_CONFIRMED_KEY(formToken), 'true');
        setShareConfirmed(true);
    };

    const handleShareCopy = async () => {
        try {
            await navigator.clipboard.writeText(currentUrl);
            setShareCopySuccess(true);
            setTimeout(() => setShareCopySuccess(false), 1800);
        } catch { /* silent */ }
    };

    // ── Stepper helpers ──────────────────────────────────────────────────────
    const StepBadge: React.FC<{ n: number; done: boolean; active: boolean }> = ({ n, done, active }) => (
        <div style={{
            width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: '0.82rem',
            background: done ? '#16a34a' : active ? '#2563eb' : '#e2e8f0',
            color: done || active ? '#fff' : '#94a3b8',
            transition: 'background 0.2s',
        }}>
            {done ? '✓' : n}
        </div>
    );

    const StepConnector = () => (
        <div style={{ width: 2, background: '#e2e8f0', flexShrink: 0, alignSelf: 'stretch', margin: '2px 13px' }} />
    );

    // ── Slot card renderer (extracted for reuse) ──────────────────────────────
    const renderSlotCards = () => (
        <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
            {slots.map((slot, idx) => {
                const isComplete = slot.isComplete;
                const isEditing = editingSlotIndex === idx;

                if (isComplete) {
                    return (
                        <div key={`slot-${idx}`} style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '8px 12px', borderRadius: 8,
                            border: `1.5px solid ${colors.badgeGreenText}`,
                            background: colors.badgeGreenBg,
                        }}>
                            <PersonIcon size={17} color={colors.badgeGreenText}/>
                            <div style={{flex: 1}}>
                                <div style={{fontWeight: 700, fontSize: '0.9rem', color: '#22543d'}}>{[slot.firstName, slot.surname].filter(Boolean).join(' ')}</div>
                            </div>
                            <span style={{fontSize: '1rem', color: colors.badgeGreenText}}>✓</span>
                        </div>
                    );
                }

                return (
                    <div key={`slot-${idx}`}>
                        <button
                            type="button"
                            onClick={() => isEditing ? closeSlot() : openSlot(idx)}
                            style={{
                                width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10,
                                padding: '9px 12px', borderRadius: isEditing ? '8px 8px 0 0' : 8,
                                border: isEditing ? `2px solid ${colors.brand}` : '1.5px solid #cbd5e0',
                                background: isEditing ? colors.brandFaint : '#f7fafc',
                                cursor: 'pointer', fontWeight: 600,
                                color: isEditing ? colors.brand : '#4a5568',
                                transition: 'all 0.15s ease',
                            }}
                        >
                            <PersonIcon size={17} color={isEditing ? colors.brand : '#718096'}/>
                            <span style={{flex: 1, fontSize: '0.9rem'}}>{t.guest} {idx + 1}</span>
                            <span style={{fontSize: '0.78rem', fontWeight: 400, color: isEditing ? colors.brand : '#94a3b8'}}>
                                {isEditing ? '▲ close' : t.fillYourDetails}
                            </span>
                        </button>

                        {isEditing && (
                            <div style={{
                                border: `2px solid ${colors.brand}`, borderTop: 'none',
                                borderRadius: '0 0 8px 8px', padding: '14px 12px', background: '#fff',
                                boxSizing: 'border-box',
                                marginLeft: -40,
                                width: 'calc(100% + 40px)',
                            }}>
                                {form?.documentScanEnabled && isMobile && (
                                    <button
                                        type="button"
                                        onClick={() => setScanning(true)}
                                        style={{
                                            display: 'block', width: '100%', marginBottom: 14,
                                            padding: '11px 16px', borderRadius: 8,
                                            border: `1.5px solid ${colors.brand}`, background: colors.brandFaint,
                                            color: colors.brand, fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
                                        }}
                                    >
                                        {t.scanDocumentButton}
                                    </button>
                                )}
                                {scanning && (
                                    <Suspense fallback={null}>
                                        <DocumentScanner
                                            validStateCodes={validStateCodes}
                                            onResult={applyMrzPrefill}
                                            onClose={() => setScanning(false)}
                                            captureLabel={t.scanCaptureLabel}
                                            capturingLabel={t.scanCapturingLabel}
                                            hintLabel={t.scanHintLabel}
                                            troubleLabel={t.scanTroubleLabel}
                                            cameraErrorLabel={t.scanCameraErrorLabel}
                                            closeAriaLabel={t.scanCloseAriaLabel}
                                        />
                                    </Suspense>
                                )}
                                <div className="form-grid">
                                    <div className="form-field">
                                        <label className="form-label">{t.firstName} *{scanTag('firstName')}</label>
                                        <input className="form-control" value={draft.firstName}
                                               placeholder={t.firstNamePlaceholder}
                                               onChange={e => onDraftChange('firstName', e.target.value)}/>
                                        {draft.firstName.trim() && !isValidNameChar(draft.firstName) && (
                                            <div style={{color: '#c53030', fontSize: '0.8rem', marginTop: 3}}>{t.invalidNameChars}</div>
                                        )}
                                    </div>
                                    <div className="form-field">
                                        <label className="form-label">{t.surname} *{scanTag('surname')}</label>
                                        <input className="form-control" value={draft.surname}
                                               placeholder={t.surnamePlaceholder}
                                               onChange={e => onDraftChange('surname', e.target.value)}/>
                                        {draft.surname.trim() && !isValidNameChar(draft.surname) && (
                                            <div style={{color: '#c53030', fontSize: '0.8rem', marginTop: 3}}>{t.invalidNameChars}</div>
                                        )}
                                    </div>
                                    <div className="form-field">
                                        <label className="form-label">{t.dateOfBirth}{scanTag('dateOfBirth')}</label>
                                        <input className="form-control" type="date"
                                               max={toLocalIsoDate(maxDateOfBirth())}
                                               value={draft.dateOfBirth}
                                               onChange={e => onDraftChange('dateOfBirth', e.target.value)}/>
                                        {draft.dateOfBirth && (() => {
                                            const dob = parseLocalDate(draft.dateOfBirth);
                                            const today = new Date();
                                            let age = today.getFullYear() - dob.getFullYear();
                                            if (today.getMonth() < dob.getMonth() ||
                                                (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())) age--;
                                            const isUnder18 = age < 18;
                                            return (
                                                <div style={{ fontSize: '0.8rem', marginTop: 3, color: isUnder18 ? '#2b6cb0' : '#276749' }}>
                                                    {isUnder18 ? t.under18 : t.over18Tax}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                    <div className="form-field" style={{ gridColumn: '1 / -1' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
                                            <input type="checkbox" checked={draft.hasDisabilityCard}
                                                onChange={e => onDraftChange('hasDisabilityCard', e.target.checked)}
                                                style={{ width: 18, height: 18, flexShrink: 0, cursor: 'pointer' }}/>
                                            <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>{t.disabilityCard}</span>
                                        </label>
                                    </div>
                                    <div className="form-field">
                                        <label className="form-label">{t.phone}</label>
                                        <input className="form-control" value={draft.phone}
                                               placeholder={t.phonePlaceholder}
                                               onChange={e => onDraftChange('phone', e.target.value)}/>
                                    </div>
                                    <div className="form-field">
                                        <label className="form-label">{t.address} *</label>
                                        <input className="form-control" value={draft.address}
                                               placeholder={t.addressPlaceholder}
                                               onChange={e => onDraftChange('address', e.target.value)}
                                               maxLength={128}/>
                                        {draft.address.trim().length > 128 && (
                                            <div style={{color: '#c53030', fontSize: '0.8rem', marginTop: 3}}>{t.addressTooLong}</div>
                                        )}
                                    </div>
                                    <div className="form-field">
                                        <label className="form-label">{t.country} *{scanTag('country')}</label>
                                        <SearchableSelect className="form-control" value={draft.country}
                                            onChange={v => onDraftChange('country', v)}
                                            options={countryOptions} placeholder={t.selectCountry}/>
                                    </div>
                                    {isCzechCitizen(draft.country) && (
                                        <div style={{gridColumn: '1 / -1', padding: '8px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: '0.85rem', color: '#1e40af'}}>
                                            {prefersCzech
                                                ? 'Český občan — není nutné vyplňovat účel pobytu ani podpis. Stačí vyplnit základní údaje a dokladový průkaz.'
                                                : 'Czech citizen — purpose of stay and signature are not required. Just fill in basic details and document information.'}
                                        </div>
                                    )}
                                    {/* Document type decides whether the visa/residence-permit fields below show at
                                        all, so it (and the rest of the document group) must come before them. */}
                                    <div className="form-field">
                                        <label className="form-label">{t.documentType} *{scanTag('documentType')}</label>
                                        <select className="form-control" value={draft.documentType}
                                                onChange={e => onDraftChange('documentType', e.target.value)}>
                                            <option value="">{t.selectDocumentType}</option>
                                            {DOCUMENT_TYPES.map(dt => (
                                                <option key={dt.value} value={dt.value}>
                                                    {prefersCzech ? dt.cs : dt.en}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-field">
                                        <label className="form-label">{t.documentNumber} *{scanTag('documentNumber')}</label>
                                        <input className="form-control" value={draft.documentNumber}
                                               placeholder={t.documentNumberPlaceholder}
                                               onChange={e => onDraftChange('documentNumber', e.target.value)} maxLength={30}/>
                                        {draft.documentNumber.trim() && !isValidDocumentNumber(draft.documentNumber) && (
                                            <div style={{color: '#c53030', fontSize: '0.8rem', marginTop: 3}}>{t.documentNumberInvalid}</div>
                                        )}
                                    </div>
                                    <div className="form-field">
                                        <label className="form-label">{t.documentCountry} *{scanTag('documentCountry')}</label>
                                        <SearchableSelect className="form-control" value={draft.documentCountry}
                                            onChange={v => onDraftChange('documentCountry', v)}
                                            options={countryOptions} placeholder={t.selectDocumentCountry}/>
                                    </div>
                                    {!isCzechCitizen(draft.country) && !isVisaExemptDocType(draft.documentType) && requiresVisa(draft.country) && (
                                        <div className="form-field">
                                            <label className="form-label">{t.visaNumber} *</label>
                                            <input className="form-control" value={draft.visaNumber}
                                                   onChange={e => onDraftChange('visaNumber', e.target.value)} maxLength={15}/>
                                            {draft.visaNumber.trim() && !isValidVisaNumber(draft.visaNumber) && (
                                                <div style={{color: '#c53030', fontSize: '0.8rem', marginTop: 3}}>{t.visaNumberInvalid}</div>
                                            )}
                                        </div>
                                    )}
                                    {draft.documentType === DOC_TYPE_GREEN_PASSPORT_EU_RESIDENCE && (
                                        <div className="form-field">
                                            <label className="form-label">{t.residencePermitNumber} *</label>
                                            <input className="form-control" value={draft.residencePermitNumber}
                                                   onChange={e => onDraftChange('residencePermitNumber', e.target.value)} maxLength={15}/>
                                            {draft.residencePermitNumber.trim() && !isValidResidencePermitNumber(draft.residencePermitNumber) && (
                                                <div style={{color: '#c53030', fontSize: '0.8rem', marginTop: 3}}>{t.residencePermitNumberInvalid}</div>
                                            )}
                                        </div>
                                    )}
                                    {!isCzechCitizen(draft.country) && (
                                        <div className="form-field">
                                            <label className="form-label">{t.selectPurpose} *</label>
                                            <select className="form-control" value={draft.purposeOfStay}
                                                    onChange={e => onDraftChange('purposeOfStay', e.target.value)}>
                                                <option value="">{t.selectPurpose}</option>
                                                {dedupedPurposes.map((p, pi) => (
                                                    <option key={`p-${p.submitValue}-${pi}`} value={p.submitValue}>
                                                        {getPreferredMappedLabel(p, prefersCzech)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    {!isCzechCitizen(draft.country) && <div style={{gridColumn: '1 / -1'}}>
                                        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6}}>
                                            <label className="form-label" style={{margin: 0}}>{t.signatureRequired}</label>
                                            <button type="button" className="btn btn-sm btn-danger"
                                                    onClick={clearSignature} style={{padding: '4px 10px'}}>
                                                {t.clearSignature}
                                            </button>
                                        </div>
                                        <div ref={blockTouchScroll} data-testid="signature-surface"
                                             style={SIGNATURE_SURFACE_STYLE} onPointerUp={onSignaturePointerUp}
                                             onPointerCancel={onSignaturePointerUp} onLostPointerCapture={onSignaturePointerUp}>
                                            <Signature key={`signature-slot-${idx}`} ref={signatureRef}
                                                onPointer={onSignaturePointer}
                                                style={{
                                                    width: '100%', display: 'block', height: SIGNATURE_REFERENCE_HEIGHT,
                                                    ['--w-signature-background' as any]: '#f8fafc',
                                                }}/>
                                        </div>
                                        <SignatureQualityNotice
                                            verdict={signatureAnalysis.verdict}
                                            confirmed={signatureConfirmedSvg !== null && signatureConfirmedSvg === (signatureSvgNow || draft.signatureSvg || '')}
                                            texts={{signatureCaptured: t.signatureCaptured, signatureHint: t.signatureHint, signatureTooSimple: t.signatureTooSimple,
                                                signatureLooksSimple: t.signatureLooksSimple, signatureConfirmedNote: t.signatureConfirmedNote,
                                                signAgain: t.signAgain, confirmUsualSignature: t.confirmUsualSignature}}
                                            onSignAgain={clearSignature}
                                            onConfirm={confirmUsualSignature}/>
                                    </div>}
                                </div>

                                {!confirmSubmit ? (
                                    <div style={{marginTop: 16}}>
                                        {showValidation && getValidationErrors().length > 0 && (
                                            <div style={{
                                                marginBottom: 12, padding: '10px 14px',
                                                background: '#fff5f5', border: '1px solid #feb2b2',
                                                borderRadius: 8, fontSize: '0.875rem', color: '#c53030'
                                            }}>
                                                <div style={{fontWeight: 700, marginBottom: 6}}>
                                                    {language === 'cs' ? 'Prosím vyplňte chybějící údaje:' : 'Please complete the following fields:'}
                                                </div>
                                                <ul style={{margin: 0, paddingLeft: 18}}>
                                                    {getValidationErrors().map((e, i) => <li key={i}>{e}</li>)}
                                                </ul>
                                            </div>
                                        )}
                                        <div style={{display: 'flex', justifyContent: 'flex-end', gap: 10}}>
                                            <button type="button" className="btn btn-sm btn-muted"
                                                    onClick={closeSlot} style={{padding: '8px 14px'}}>
                                                {t.cancel}
                                            </button>
                                            <button type="button" className="btn btn-sm btn-primary"
                                                onClick={() => {
                                                    if (!isDraftComplete()) { setShowValidation(true); }
                                                    else { setShowValidation(false); setConfirmSubmit(true); }
                                                }}
                                                style={{padding: '8px 18px', fontSize: '0.98em'}}>
                                                {t.submitMyRegistration}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{
                                        marginTop: 16, padding: 14, borderRadius: 8,
                                        background: '#fffbeb', border: '1.5px solid #d97706'
                                    }}>
                                        <div style={{fontWeight: 700, color: '#92400e', marginBottom: 6}}>{t.confirmSubmitTitle}</div>
                                        <div style={{fontSize: '0.9rem', color: '#78350f', marginBottom: 12}}>{t.confirmSubmitMessage}</div>
                                        <div style={{display: 'flex', gap: 10}}>
                                            <button type="button" className="btn btn-sm btn-muted"
                                                    onClick={() => setConfirmSubmit(false)} disabled={submitting}>
                                                {t.cancel}
                                            </button>
                                            <button type="button" className="btn btn-sm btn-primary"
                                                    onClick={onConfirmSubmitSlot} disabled={submitting}
                                                    style={{padding: '8px 18px'}}>
                                                {submitting ? '...' : t.confirmSubmitYes}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );

    return (
        <div style={{maxWidth: 700, margin: '0 auto', paddingBottom: 40, overflowX: 'hidden'}}>

            {/* ── Branded gradient header ─────────────────────────────────── */}
            <div style={{
                background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
                borderRadius: '0 0 24px 24px',
                padding: '20px 16px 24px',
                marginBottom: -16,
                color: '#fff',
            }}>
                <div style={{display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20}}>
                    <div style={{
                        width: 36, height: 36, borderRadius: 10,
                        background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                        </svg>
                    </div>
                    <span style={{fontWeight: 800, fontSize: '1.1rem', letterSpacing: '-0.01em'}}>
                        Guestbook<span style={{color: '#93c5fd'}}>.cz</span>
                    </span>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap'}}>
                    <div>
                        <h2 style={{margin: '0 0 6px', fontSize: '1.35rem', fontWeight: 800, letterSpacing: '-0.01em'}}>{t.title}</h2>
                        {form && (
                            <div style={{fontSize: '0.9rem', opacity: 0.85, lineHeight: 1.6}}>
                                <div><strong>{form.apartmentName}</strong></div>
                                <div>{t.stay}: {form.checkInDate} – {form.checkOutDate}</div>
                                {form.email && <div>{t.email}: {form.email}</div>}
                            </div>
                        )}
                    </div>
                    <div style={{flexShrink: 0}}>
                        {isMobile ? (
                            <select value={language} onChange={e => setLanguage(e.target.value as GuestFormLanguage)}
                                style={{
                                    background: 'rgba(255,255,255,0.2)', border: '1.5px solid rgba(255,255,255,0.5)',
                                    borderRadius: 8, color: '#fff', fontWeight: 700, fontSize: '0.82rem',
                                    padding: '5px 8px', cursor: 'pointer', backdropFilter: 'blur(4px)', outline: 'none',
                                }}>
                                <option value="cs" style={{color: '#0f172a', background: '#fff'}}>{t.czech}</option>
                                <option value="en" style={{color: '#0f172a', background: '#fff'}}>{t.english}</option>
                            </select>
                        ) : (
                            <div style={{display: 'flex', gap: 6}}>
                                {(['cs', 'en'] as GuestFormLanguage[]).map(lang => (
                                    <button key={lang} type="button" onClick={() => setLanguage(lang)} style={{
                                        padding: '5px 12px', borderRadius: 8, cursor: 'pointer', fontWeight: 700,
                                        fontSize: '0.82rem',
                                        border: language === lang ? '1.5px solid rgba(255,255,255,0.8)' : '1.5px solid rgba(255,255,255,0.3)',
                                        background: language === lang ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)',
                                        color: '#fff', backdropFilter: 'blur(4px)',
                                    }}>
                                        {lang === 'cs' ? t.czech : t.english}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Main card ──────────────────────────────────────────────── */}
            <div style={{
                background: '#fff', borderRadius: 20,
                boxShadow: '0 4px 24px rgba(15,23,42,0.10)',
                padding: '28px 16px 20px', margin: '0 4px',
            }}>

            {/* Alerts */}
            {error && (
                <>
                    <div style={{background: '#fef2f2', color: '#991b1b', padding: '10px 14px', borderRadius: 10, marginBottom: 8, border: '1px solid #fecaca', fontSize: '0.9rem'}}>{error}</div>
                    {form && (() => {
                        const contact = getContactInfo(form);
                        if (!contact.name && !contact.email && !contact.phone) return null;
                        return (
                            <div style={{background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: '0.88rem', color: '#1e3a8a'}}>
                                <div style={{fontWeight: 700, marginBottom: 4}}>{t.guestFooterAssistance}</div>
                                {contact.name && <div><strong>{contact.name}</strong></div>}
                                {contact.email && <div>📧 {contact.email}</div>}
                                {contact.phone && <div>📞 {contact.phone}</div>}
                            </div>
                        );
                    })()}
                </>
            )}
            {success && <div style={{background: '#f0fdf4', color: '#166534', padding: '10px 14px', borderRadius: 10, marginBottom: 12, border: '1px solid #bbf7d0', fontSize: '0.9rem'}}>{success}</div>}

            {/* Email capture — shown when manager sent link directly (no email on form yet) */}
            {form && !form.email && (
                <div style={{marginBottom: 20, padding: '16px 18px', background: '#fffbeb', border: '1.5px solid #d97706', borderRadius: 12}}>
                    <div style={{fontWeight: 700, color: '#92400e', marginBottom: 4, fontSize: '1rem'}}>
                        {language === 'cs' ? 'Zadejte svůj e-mail' : 'Enter your email'}
                    </div>
                    <div style={{fontSize: '0.88rem', color: '#78350f', marginBottom: 12}}>
                        {language === 'cs'
                            ? 'Tento formulář byl odeslán přímo. Zadejte svůj e-mail — po potvrzení se nedá změnit.'
                            : 'This form was sent directly to you. Enter your email — it cannot be changed once confirmed.'}
                    </div>
                    <form onSubmit={handleSetEmail} style={{display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start'}}>
                        <input type="email" required value={emailDraft} onChange={e => setEmailDraft(e.target.value)}
                            placeholder={language === 'cs' ? 'vas@email.cz' : 'your@email.com'}
                            style={{flex: 1, minWidth: 200, padding: '9px 12px', border: '1px solid #d97706', borderRadius: 8, fontSize: '0.95rem'}}/>
                        <button type="submit" disabled={emailSubmitting} style={{
                            padding: '9px 20px', background: '#d97706', color: '#fff',
                            border: 'none', borderRadius: 8, fontWeight: 700, cursor: emailSubmitting ? 'not-allowed' : 'pointer'
                        }}>
                            {emailSubmitting ? '…' : (language === 'cs' ? 'Potvrdit' : 'Confirm')}
                        </button>
                    </form>
                    {emailError && <div style={{color: '#c53030', fontSize: '0.85rem', marginTop: 8}}>{emailError}</div>}
                </div>
            )}

            {/* ── Vertical stepper ─────────────────────────────────────────── */}
            <div>

                {/* ── Step 1: Share ────────────────────────────────────────── */}
                <div style={{display: 'flex', gap: 0}}>
                    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', width: 28, flexShrink: 0}}>
                        <StepBadge n={1} done={step1Done} active={!step1Done} />
                        <div style={{width: 2, flex: 1, background: '#e2e8f0', minHeight: 16, marginTop: 4}} />
                    </div>
                    <div style={{flex: 1, paddingLeft: 12, paddingBottom: 20}}>
                        {step1Done ? (
                            <div style={{paddingTop: 4, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'}}>
                                <span style={{color: '#16a34a', fontWeight: 600, fontSize: '0.92rem'}}>{t.step1DoneLabel}</span>
                                <button type="button" onClick={handleShareCopy} style={{
                                    padding: '4px 12px', borderRadius: 6,
                                    border: '1.5px solid #cbd5e0',
                                    background: shareCopySuccess ? '#dcfce7' : '#f8fafc',
                                    color: shareCopySuccess ? '#16a34a' : '#475569',
                                    cursor: 'pointer', fontWeight: 600, fontSize: '0.78rem', whiteSpace: 'nowrap',
                                }}>
                                    {shareCopySuccess ? '✔ Copied' : '🔗 Copy link'}
                                </button>
                            </div>
                        ) : (
                            <>
                                <div style={{fontWeight: 700, fontSize: '1rem', color: '#0f172a', paddingTop: 2}}>{t.step1Title}</div>
                                <div style={{fontSize: '0.82rem', color: '#64748b', marginBottom: 12}}>{t.step1Subtitle}</div>
                                <div style={{
                                    background: '#f7fafc', border: '1px solid #cbd5e0', borderRadius: 8,
                                    padding: '10px 12px', marginBottom: 10,
                                    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                                }}>
                                    <span style={{flex: 1, wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.82rem', color: '#2d3748'}}>
                                        {currentUrl}
                                    </span>
                                    <button type="button" onClick={handleShareCopy} style={{
                                        padding: '5px 12px', borderRadius: 6, border: '1px solid #cbd5e0',
                                        background: shareCopySuccess ? '#c6f6d5' : '#fff',
                                        color: shareCopySuccess ? '#276749' : '#2d3748',
                                        cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem', whiteSpace: 'nowrap',
                                    }}>
                                        {shareCopySuccess ? '✔ Copied' : t.copyUrl}
                                    </button>
                                </div>
                                {typeof navigator.share === 'function' && (
                                    <button type="button"
                                        onClick={() => navigator.share({ url: currentUrl, title: 'Guest registration form' })}
                                        style={{
                                            width: '100%', padding: '9px', marginBottom: 10, borderRadius: 8,
                                            border: '1px solid #3182ce', background: '#ebf8ff', color: '#2b6cb0',
                                            fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
                                        }}>
                                        {t.step1NativeShare}
                                    </button>
                                )}
                                <button type="button" onClick={confirmShare} style={{
                                    width: '100%', padding: '10px', borderRadius: 8, border: 'none',
                                    background: '#16a34a', color: '#fff', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer',
                                }}>
                                    {t.step1ConfirmBtn}
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* ── Step 2: Registration ─────────────────────────────────── */}
                <div style={{display: 'flex', gap: 0}}>
                    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', width: 28, flexShrink: 0}}>
                        <StepBadge n={2} done={allDone} active={step1Done && !allDone} />
                        {(guideEnabled || taxRequired) && <div style={{width: 2, flex: 1, background: '#e2e8f0', minHeight: 16, marginTop: 4}} />}
                    </div>
                    <div style={{flex: 1, paddingLeft: 12, paddingBottom: guideEnabled ? 20 : 0}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: 8, paddingTop: 2, marginBottom: step1Done ? 12 : 4}}>
                            <span style={{fontWeight: 700, fontSize: '1rem', color: step1Done ? '#0f172a' : '#94a3b8'}}>
                                {allDone ? t.step2DoneLabel : t.step2Title}
                            </span>
                            {step1Done && !allDone && (
                                <span style={{fontSize: '0.8rem', color: '#64748b'}}>
                                    {completedCount}/{totalCount}
                                </span>
                            )}
                        </div>

                        {step1Done && renderSlotCards()}
                    </div>
                </div>

                {/* ── Step 3: City tax (only when the apartment collects it) — renders in place instead
                     of the old auto-redirect, so the guest sees exactly what's happening. Stays
                     visible to the owner even when exempt (to manage the override), but doesn't
                     claim a step number in that case — the guide step shrinks back to take its
                     place, since taxRequired already accounts for exemption. ─────── */}
                {(taxRequired || (isOwner && taxCollectionEnabled)) && (
                    <div style={{display: 'flex', gap: 0}}>
                        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', width: 28, flexShrink: 0}}>
                            {taxRequired && <StepBadge n={3} done={taxPaid} active={allDone && !taxPaid} />}
                            {guideEnabled && <div style={{width: 2, flex: 1, background: '#e2e8f0', minHeight: 16, marginTop: 4}} />}
                        </div>
                        <div style={{flex: 1, paddingLeft: 12, paddingBottom: guideEnabled ? 20 : 0}}>
                            {taxRequired && (
                            <div style={{fontWeight: 700, fontSize: '1rem', color: allDone ? '#0f172a' : '#94a3b8', marginBottom: 10}}>
                                {taxPaid ? t.taxStepDoneLabel : t.taxStepTitle}
                            </div>
                            )}

                            {taxRequired && allDone && (
                                confirmingTaxPayment ? (
                                    <p style={{color: '#2563eb', fontWeight: 600, fontSize: '0.88rem', margin: 0}}>{t.taxConfirming}</p>
                                ) : taxPaid ? (
                                    <div style={{display: 'flex', alignItems: 'center', gap: 8, color: '#16a34a', fontWeight: 700, fontSize: '0.9rem'}}>
                                        <span>✓</span>
                                        <span>{fmtTaxAmount(taxSummary!.amount)} {taxSummary!.currency}</span>
                                    </div>
                                ) : taxSummaryLoading ? (
                                    <p style={{color: '#64748b', fontSize: '0.85rem', margin: 0}}>{t.loading}</p>
                                ) : taxSummaryError || !taxSummary ? (
                                    <p style={{color: '#dc2626', fontSize: '0.85rem', margin: 0}}>{t.taxSummaryError}</p>
                                ) : (
                                    <>
                                        {taxSummary.amountPaid > 0 && (
                                            <div style={{background: '#fffbeb', border: '1.5px solid #fcd34d', borderRadius: 10, padding: '12px 14px', marginBottom: 12}}>
                                                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#92400e', marginBottom: 3}}>
                                                    <span>{t.taxAlreadyPaidLabel}</span>
                                                    <span style={{fontWeight: 700}}>{fmtTaxAmount(taxSummary.amountPaid)} {taxSummary.currency}</span>
                                                </div>
                                                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#92400e', fontWeight: 700}}>
                                                    <span>{t.taxMissingLabel}</span>
                                                    <span>{fmtTaxAmount(taxSummary.missingAmount)} {taxSummary.currency}</span>
                                                </div>
                                                <p style={{fontSize: '0.76rem', color: '#78350f', marginTop: 8, marginBottom: 0, lineHeight: 1.4}}>{t.taxPartialNotice}</p>
                                            </div>
                                        )}
                                        <div style={{background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px 10px', marginBottom: 12}}>
                                            <div style={{fontSize: '0.72rem', color: '#94a3b8', marginBottom: 8}}>
                                                {taxSummary.apartmentName} · {taxSummary.checkInDate}–{taxSummary.checkOutDate} ({taxSummary.nights} {t.taxNights})
                                            </div>
                                            {taxSummary.adultCount > 0 && (
                                                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#475569', marginBottom: 5}}>
                                                    <span>{taxSummary.adultCount} {t.taxAdults} × {taxSummary.nights} × {fmtTaxAmount(taxSummary.adultRate)} {taxSummary.currency}</span>
                                                    <span>{fmtTaxAmount(taxSummary.adultSubtotal)} {taxSummary.currency}</span>
                                                </div>
                                            )}
                                            {taxSummary.childCount > 0 && (
                                                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#475569', marginBottom: 5}}>
                                                    <span>{taxSummary.childCount} {t.taxChildren} × {taxSummary.nights} × {fmtTaxAmount(taxSummary.childRate)} {taxSummary.currency}</span>
                                                    <span>{fmtTaxAmount(taxSummary.childSubtotal)} {taxSummary.currency}</span>
                                                </div>
                                            )}
                                            {taxSummary.disabledCount > 0 && (
                                                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#475569', marginBottom: 5}}>
                                                    <span>{taxSummary.disabledCount} {t.taxDisabled} × {taxSummary.nights} × {fmtTaxAmount(taxSummary.disabledRate)} {taxSummary.currency}</span>
                                                    <span>{fmtTaxAmount(taxSummary.disabledSubtotal)} {taxSummary.currency}</span>
                                                </div>
                                            )}
                                            <div style={{borderTop: '1px solid #e2e8f0', paddingTop: 8, marginTop: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline'}}>
                                                <span style={{fontWeight: 700, color: '#1e293b', fontSize: '0.86rem'}}>{t.taxTotal}</span>
                                                <span style={{fontWeight: 800, color: '#2563eb', fontSize: '1.1rem'}}>{fmtTaxAmount(taxSummary.amount)} {taxSummary.currency}</span>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handlePayTax}
                                            disabled={payingTax}
                                            style={{
                                                display: 'block', width: '100%', border: 'none', cursor: payingTax ? 'not-allowed' : 'pointer',
                                                padding: '13px', borderRadius: 10, fontWeight: 700, fontSize: '0.92rem', color: '#fff',
                                                background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)', opacity: payingTax ? 0.7 : 1,
                                            }}
                                        >
                                            {payingTax ? t.taxPaying : (taxSummary.amountPaid > 0 ? t.taxPayMissingButton : t.taxPayButton)}
                                        </button>
                                        <p style={{color: '#94a3b8', fontSize: '0.72rem', marginTop: 9, marginBottom: 0, lineHeight: 1.45}}>{t.taxStripeNotice}</p>
                                    </>
                                )
                            )}

                            {isOwner && (() => {
                                const currentTaxStatus: 'PAID' | 'EXEMPT' | 'UNPAID' = taxExempt ? 'EXEMPT' : taxPaid ? 'PAID' : 'UNPAID';
                                return (
                                <div style={{marginTop: taxRequired ? 10 : 0}}>
                                    <div style={{fontSize: '0.75rem', color: '#64748b', marginBottom: 5, fontWeight: 600}}>
                                        {t.taxManagerOverrideLabel}
                                    </div>
                                    {taxExempt && (
                                        <p style={{fontSize: '0.78rem', color: '#475569', margin: '0 0 8px'}}>{t.taxExemptActiveNotice}</p>
                                    )}
                                    <div style={{display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8}}>
                                        {([
                                            {status: 'PAID' as const, label: `✓ ${t.taxOverridePaid}`, active: {bg: '#dcfce7', border: '#16a34a', color: '#166534'}},
                                            {status: 'EXEMPT' as const, label: `— ${t.taxOverrideExempt}`, active: {bg: '#e0e7ff', border: '#6366f1', color: '#3730a3'}},
                                            {status: 'UNPAID' as const, label: `✕ ${t.taxOverrideUnpaid}`, active: {bg: '#fee2e2', border: '#dc2626', color: '#991b1b'}},
                                        ]).map(opt => {
                                            const isActive = currentTaxStatus === opt.status;
                                            return (
                                                <button
                                                    key={opt.status}
                                                    type="button"
                                                    disabled={settingTaxStatus}
                                                    onClick={() => handleSetTaxPaymentStatus(opt.status)}
                                                    style={{
                                                        background: isActive ? opt.active.bg : '#fff',
                                                        border: `1px solid ${isActive ? opt.active.border : '#e2e8f0'}`,
                                                        borderRadius: 8, cursor: settingTaxStatus ? 'not-allowed' : 'pointer', fontSize: '0.78rem',
                                                        color: isActive ? opt.active.color : '#94a3b8',
                                                        padding: '5px 11px', fontWeight: isActive ? 700 : 500,
                                                        opacity: settingTaxStatus ? 0.6 : 1,
                                                    }}
                                                >
                                                    {opt.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <div>
                                        <div style={{fontSize: '0.72rem', color: '#94a3b8', marginBottom: 4}}>{t.taxSetAmountLabel}</div>
                                        <div style={{display: 'flex', gap: 6}}>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={taxAmountInput}
                                                onChange={e => setTaxAmountInput(e.target.value)}
                                                placeholder={taxSummary ? fmtTaxAmount(taxSummary.amountPaid) : '0'}
                                                style={{
                                                    width: 110, padding: '6px 9px', borderRadius: 8, border: '1px solid #e2e8f0',
                                                    fontSize: '0.82rem', color: '#1e293b',
                                                }}
                                            />
                                            <button
                                                type="button"
                                                disabled={settingTaxStatus || taxAmountInput.trim() === '' || isNaN(Number(taxAmountInput))}
                                                onClick={() => {
                                                    const parsed = Number(taxAmountInput);
                                                    handleSetTaxPaymentStatus('PAID', parsed);
                                                    setTaxAmountInput('');
                                                }}
                                                style={{
                                                    padding: '6px 14px', borderRadius: 8, border: '1px solid #94a3b8', background: '#fff',
                                                    color: '#334155', fontSize: '0.78rem', fontWeight: 700,
                                                    cursor: (settingTaxStatus || taxAmountInput.trim() === '') ? 'not-allowed' : 'pointer',
                                                    opacity: settingTaxStatus ? 0.6 : 1,
                                                }}
                                            >
                                                {t.taxSetAmountButton}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                );
                            })()}
                        </div>
                    </div>
                )}

                {/* ── Step 3/4: Guide (visible when guide enabled; content hidden until step 1 done) ─────── */}
                {guideEnabled && (
                    <div style={{display: 'flex', gap: 0}}>
                        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', width: 28, flexShrink: 0}}>
                            <StepBadge n={guideStepN} done={false} active={(step1Done || isOwner) && guideAccessible} />
                        </div>
                        <div style={{flex: 1, paddingLeft: 12, paddingTop: 2}}>
                            <div style={{fontWeight: 700, fontSize: '1rem', color: guideAccessible ? '#0f172a' : '#94a3b8', marginBottom: 10}}>
                                {t.step3Title}
                            </div>
                        {(step1Done || isOwner) && (<>

                            {guideAccessible ? (
                                <a href={`/guide/${formToken}`} style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                                    padding: '16px 24px', borderRadius: 12, textDecoration: 'none', cursor: 'pointer',
                                    background: 'linear-gradient(135deg, #92400e 0%, #d97706 100%)',
                                    color: '#fff', fontWeight: 700, fontSize: '1rem', letterSpacing: '0.01em',
                                }}>
                                    <span className="guide-icon-animated" style={{ display: 'inline-flex' }}>
                                        <KeyIcon size={22} color="#fff" />
                                    </span>
                                    <span>{t.viewGuide}</span>
                                </a>
                            ) : (
                                <button type="button" onClick={() => setShowGuideLockedPopup(true)} style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                                    padding: '16px 24px', borderRadius: 12, border: 'none', cursor: 'pointer',
                                    background: 'linear-gradient(135deg, #92400e 0%, #d97706 100%)',
                                    color: '#fff', fontWeight: 700, fontSize: '1rem', letterSpacing: '0.01em',
                                    width: '100%', opacity: 0.82,
                                }}>
                                    <KeyIcon size={22} color="#fff" />
                                    <span>{t.viewGuide}</span>
                                </button>
                            )}

                            {isOwner && (
                                <div style={{marginTop: 10}}>
                                    <div style={{fontSize: '0.75rem', color: '#64748b', marginBottom: 5, fontWeight: 600}}>
                                        Guide access for guests
                                    </div>
                                    <div style={{display: 'flex', gap: 6, flexWrap: 'wrap'}}>
                                        {([
                                            {mode: 'AUTO' as const, label: '⚙️ Automatic', active: {bg: '#f1f5f9', border: '#64748b', color: '#334155'}},
                                            {mode: 'OPEN' as const, label: '🔓 Force open', active: {bg: '#dcfce7', border: '#16a34a', color: '#166534'}},
                                            {mode: 'BLOCKED' as const, label: '🔒 Blocked', active: {bg: '#fee2e2', border: '#dc2626', color: '#991b1b'}},
                                        ]).map(opt => {
                                            const isActive = guideAccessMode === opt.mode;
                                            return (
                                                <button
                                                    key={opt.mode}
                                                    type="button"
                                                    onClick={() => handleSetGuideAccessMode(opt.mode)}
                                                    style={{
                                                        background: isActive ? opt.active.bg : '#fff',
                                                        border: `1px solid ${isActive ? opt.active.border : '#e2e8f0'}`,
                                                        borderRadius: 8, cursor: 'pointer', fontSize: '0.78rem',
                                                        color: isActive ? opt.active.color : '#94a3b8',
                                                        padding: '5px 11px', fontWeight: isActive ? 700 : 500,
                                                    }}
                                                >
                                                    {opt.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </>)}
                        </div>
                    </div>
                )}

            </div>{/* end stepper */}

            </div>{/* end main card */}

            {/* Contact footer */}
            {form && (() => {
                const contact = getContactInfo(form);
                if (!contact.name && !contact.email && !contact.phone) return null;
                return (
                    <div style={{
                        margin: '12px 4px 0', padding: '14px 16px',
                        background: '#eff6ff', borderRadius: 12, border: '1px solid #bfdbfe'
                    }}>
                        <div style={{fontWeight: 700, marginBottom: 8, color: '#1e40af', fontSize: '0.9rem'}}>
                            {t.guestFooterAssistance}
                        </div>
                        <div style={{fontSize: '0.9rem', color: '#1e3a8a', lineHeight: 1.7}}>
                            {contact.name && <div><strong>{contact.name}</strong></div>}
                            {contact.email && <div>📧 {contact.email}</div>}
                            {contact.phone && <div>📞 {contact.phone}</div>}
                        </div>
                    </div>
                );
            })()}

            {/* ── Guide locked popup ──────────────────────────────────────── */}
            {showGuideLockedPopup && (
                <div
                    onClick={() => setShowGuideLockedPopup(false)}
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 1000, padding: '0 20px',
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: '#fff', borderRadius: 20, padding: '32px 24px 24px',
                            maxWidth: 360, width: '100%', textAlign: 'center',
                            boxShadow: '0 8px 40px rgba(0,0,0,0.22)',
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'center', color: '#d97706', marginBottom: 12 }}>
                            <KeyIcon size={44} color="currentColor" />
                        </div>
                        <div style={{ fontWeight: 800, fontSize: '1.15rem', color: '#0f172a', marginBottom: 10 }}>
                            {t.step3Title}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#64748b', lineHeight: 1.6, marginBottom: 24 }}>
                            {taxRequired && !taxPaid ? t.step3LockedHintTax : t.step3LockedHint}
                        </div>
                        <button
                            type="button"
                            onClick={() => setShowGuideLockedPopup(false)}
                            style={{
                                padding: '11px 32px', background: '#2563eb', color: '#fff',
                                border: 'none', borderRadius: 10, fontWeight: 700,
                                cursor: 'pointer', fontSize: '0.95rem', width: '100%',
                            }}
                        >
                            {t.guideLockedPopupBtn}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PublicCheckinForm;

